import User, { UserOptions } from 'models/User';

import AIArtifact, { AIArtifactOptions } from './AIArtifact';

export enum AIJobStatus {
  AWAITING = 'awaiting',
  COMPLETED = 'completed',
  ERRORED = 'error',
  PENDING = 'pending',
  RUNNING = 'running',
}

export enum AIJobType {
  EXTRACT_DATA = 'extract_data',
  PATIENT_DEMOGRAPHICS = 'patient_demographics',
  ATTRIBUTE_EXTRACTION = 'attribute_extraction',
  SUMMARY = 'summary',
  COMPARISON = 'comparison',
  CRITERIA_EVALUATION = 'criteria_evaluation',
}

export type AICriteriaCategorySnapshot = {
  /**
   * Absent on snapshots written before OLIO-2697 — there is deliberately no backfill.
   * Category names are unique only within a set, so this is the only reliable way to tell a
   * group category from a same-named inherited client one.
   */
  id?: string;
  name: string;
  color: string;
  order: number;
};

export type AIJobCriterion = {
  id: string;
  aiCriteria: {
    id: string;
    order: number;
    category: string;
    criterion: string;
    description?: string;
  };
  /** Optional only until OLIO-2697's api change is deployed — an older api omits it entirely. */
  aiCriteriaCategory?: AICriteriaCategorySnapshot;
  /**
   * @deprecated OLIO-2697 — replaced by aiCriteriaCategory. Removed in OLIO-2698.
   * Required, not optional: the api dual-emits both keys for the whole transition, so this is
   * always present and is what makes reading a category total rather than fallible.
   */
  aiCriteriaSetCategory: AICriteriaCategorySnapshot;
  // The AI's reviewer-facing clinical statement about the patient, and how strongly the criteria is
  // evidenced ('strong' | 'likely' | 'possible'). Both null on criteria evaluated before the
  // evidence enrichment landed; the card renders without its disclosure in that case.
  finding: string | null;
  matchStrength: string | null;
  sources: { attachmentId: string; displayId: number }[];
};

export interface AIJobOptions {
  id: string;
  type?: AIJobType;
  reviewId?: string;
  aiArtifact?: AIArtifactOptions;
  aiJobbableId: string;
  createdBy?: UserOptions;
  createdByGroup?: {
    id: string;
    name: string;
  };
  error?: string | { type: string; message: string };
  config?: {
    attachmentIds?: string[];
    aiCriteriaSetId?: string;
  };
  createdAt?: Date | null;
  updatedAt?: Date | null;
  status?: AIJobStatus;
  hasCollapsedPromptExplainer?: boolean;
  isRetry?: boolean;
  aiJobCriteria?: AIJobCriterion[];
}

/**
 * @class AIJob
 * @classdesc Represents an AI job in the system
 * @property {string} id - The job's id
 * @property {AIJobType | null} type - The job's type
 * @property {string | null} reviewId - The associated review id
 * @property {AIArtifact | null} aiArtifact - The associated AI artifact
 * @property {Partial<User> | null} createdBy - The user who created the job
 * @property {Object | null} createdByGroup - The group that created the job
 * @property {Date | null} createdAt - The job's creation date
 * @property {AIJobStatus | null} status - The job's status
 * @property {boolean | null} hasCollapsedPromptExplainer - Whether the job's prompt explainer has been collapsed
 * @param {Partial<AIJobOptions>} [options={}]
 */
export default class AIJob {
  id: string;
  type: AIJobType | null;
  reviewId: string | null;
  aiArtifact: AIArtifact | null;
  aiJobbableId: string | null;
  createdBy: Partial<User> | null;
  createdByGroup: {
    id: string;
    name: string;
  } | null;
  config: {
    attachmentIds?: string[];
    aiCriteriaSetId?: string;
  } | null;
  createdAt: Date | null;
  error?: string | { type: string; message: string };
  updatedAt: Date | null;
  status: AIJobStatus | null;
  hasCollapsedPromptExplainer: boolean | null;
  isRetry: boolean;
  aiJobCriteria: AIJobCriterion[];

  constructor(options?: Partial<AIJobOptions>) {
    const opts = { ...options };
    this.id = opts.id || '';
    this.type = opts.type || null;
    this.reviewId = opts.reviewId || null;
    this.aiArtifact = opts.aiArtifact ? new AIArtifact(opts.aiArtifact) : null;
    this.aiJobbableId = opts.aiJobbableId || null;
    this.createdBy = opts.createdBy ? new User(opts.createdBy) : null;
    this.createdByGroup = opts.createdByGroup || null;
    this.config = opts.config || null;
    this.createdAt = opts.createdAt || null;
    this.updatedAt = opts.updatedAt || null;
    this.status = opts.status || null;
    this.hasCollapsedPromptExplainer = opts.hasCollapsedPromptExplainer ?? null;
    this.error = opts.error;
    this.isRetry = opts.isRetry ?? false;
    this.aiJobCriteria = opts.aiJobCriteria || [];
  }

  get attachmentIds() {
    return this.config?.attachmentIds || [];
  }

  get isAwaiting() {
    return this.status === AIJobStatus.AWAITING;
  }

  get isPending() {
    return this.status === AIJobStatus.PENDING;
  }

  get isRunning() {
    return this.status === AIJobStatus.RUNNING;
  }

  get isProcessing() {
    return this.isAwaiting || this.isPending || this.isRunning;
  }

  get isCompleted() {
    return this.status === AIJobStatus.COMPLETED;
  }

  get isErrored() {
    return this.status === AIJobStatus.ERRORED;
  }

  get isMultiplePatientDemographicsError() {
    return (
      typeof this.error === 'object' &&
      this.error !== null &&
      (this.error as { type?: string }).type === 'MultiplePatientDemographicsError'
    );
  }

  serialize() {
    return {
      id: this.id,
      type: this.type,
      reviewId: this.reviewId,
      aiArtifact: this.aiArtifact?.serialize() || null,
      aiJobbableId: this.aiJobbableId,
      createdBy: this.createdBy,
      createdByGroup: this.createdByGroup,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      status: this.status,
      hasCollapsedPromptExplainer: this.hasCollapsedPromptExplainer,
    };
  }
}
